import { toAbsoluteUrl } from "@/regira_modules/utilities/http-utility";
import { getRequiredProductNodes, findProduct, isChoiceProductRoot } from "./data-functions";
import { groupBy, sum } from "@/regira_modules/utilities/array-utility";
import { useAxios } from "@/infrastructure/api-axios";
import { useConfig } from "@/app-config";

export const newOrder = () => ({
  id: "",
  isNewCustomer: false,
  orderlines: [],
  klasId: "",
  parent: { givenName: "", familyName: "", email: "" },
  student: { givenName: "", familyName: "", email: "" },
  comments: "",
  checkoutUrl: "",
  created: new Date(),
  paymentStatus: "",
});

export const details = async (orderId, appId) => {
  const appConfig = useConfig();
  const orderUrl = appConfig.orderDetailsUrl.replace("{appId}", encodeURIComponent(appId)).replace("{orderId}", encodeURIComponent(orderId));
  const apiAxios = useAxios();
  const response = await apiAxios.get(orderUrl);
  return response.data;
};
export async function getLastPredefinedPaidOrder(guid, appId) {
  const { lastPaidOrderUrl } = useConfig();
  const url = lastPaidOrderUrl.replace("{appId}", encodeURIComponent(appId)).replace("{guid}", encodeURIComponent(guid));
  const axios = useAxios();
  const response = await axios.get(url);
  return response.data;
}

export const ensureRequiredQuantity = (quantity, productNode) => Math.max(productNode.value.requiredQuantity || 1, quantity);

export const getOrderFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const orderParam = urlParams.get("order");
  if (orderParam) {
    try {
      const orderJson = atob(orderParam);
      const order = JSON.parse(orderJson);
      // assign internalId or externalId
      if (order != null && order.student != null) {
        order.student.number = order.student.internalId || order.student.externalId || order.internalId || order.externalId;
      }
      return {
        ...newOrder(),
        ...(order || {}),
      };
    } catch {
      console.warn("Parsing order json failed", { orderParam });
    }
  }

  return null;
};

export const getOrderedOffspring = (productNode, orderlines) => {
  return productNode.getOffspring().filter((o) => orderlines.some((ol) => ol.productId === o.value.id));
};
export const hasOrderedOffspring = (productNode, orderlines) => {
  return orderlines.some((ol) => isChoiceProductRoot(productNode) && productNode.getOffspring().some((pc) => ol.productId === pc.value.id));
};
export const isOrdered = (productNode, orderlines, checkOffspring = true) => {
  const isSelfOrdered = orderlines.some((ol) => ol.productId === productNode.value.id && ol.quantity > 0);
  return isSelfOrdered || (checkOffspring && hasOrderedOffspring(productNode, orderlines));
};
export const calcTotal = (orderlines, productNodes, categoryId = null) => {
  const orderlinesWithProducts = orderlines.map((ol) => ({ ...ol, productNode: productNodes.find((pn) => pn.value.id === ol.productId) }));
  const productsWithQuantity = orderlinesWithProducts
    .filter((ol) => !categoryId || ol.productNode.value.categoryId === categoryId)
    .map((ol) => ({ costPerUnitWithVat: ol.productNode.value.costPerUnitWithVat, quantity: ol.quantity || 0 }));
  return sum(productsWithQuantity, (x) => x.costPerUnitWithVat * x.quantity) || 0;
};

export const groupOrderlines = (orderlines) => {
  return groupBy(orderlines, (ol) => ol.productNode.value.categoryId);
};

export const validateOrderlines = (orderlines, productTree) => {
  const requiredProductNodes = getRequiredProductNodes(productTree);
  return requiredProductNodes.every((pn) => isOrdered(pn, orderlines));
};

// transforms order to new format
export const ensureLatestOrderVersion = (order) => {
  if (!order) {
    return newOrder();
  }
  const processedOrder = { ...order };
  if (processedOrder.version || "v1" === "v1") {
    // legacy klas.id
    if (order.klas) {
      processedOrder.klasId = order.klas.id;
    }
    if (order.orderlines) {
      processedOrder.orderlines = order.orderlines.map((ol) => ({
        ...ol,
        productId: ol.productId || (ol.product && ol.product.id),
      }));
    }
  }
  return processedOrder;
};
// legacy order (needed for backend)
export const ensureLegacyOrderVersion = (order, products) => {
  return {
    ...order,
    klas: { id: order.klasId },
    orderlines: order.orderlines
      .map((ol) => {
        const product = findProduct(products, ol.productId);
        product == null && console.warn(`could not find product '${ol.productId}'`, { orderline: ol });
        return {
          product: product && {
            id: product.id,
            title: product.title,
            parentProductId: product.parentProductId,
            klasId: product.klasId,
            categoryId: product.categoryId,
          },
          quantity: ol.quantity,
        };
      })
      .filter((ol) => ol.product != null),
  };
};

export const save = async (order, appId, redirectUrl) => {
  const appConfig = useConfig();
  redirectUrl = redirectUrl.startsWith("http") ? redirectUrl : toAbsoluteUrl(`${appConfig.baseUrl}/${redirectUrl}`);

  const url = `${appConfig.checkoutUrl
    .replace("{appId}", encodeURIComponent(appId))
    .replace("{orderId}", encodeURIComponent(order.id || ""))
    .replace("{clientUrl}", encodeURIComponent(redirectUrl))}`;
  const axios = useAxios();
  const response = await axios.post(url, order);
  const paymentResponse = response.data.paymentResponse;
  const saved = {
    ...order,
    id: paymentResponse.orderId,
  };
  return {
    saved,
    checkoutUrl: paymentResponse.checkoutUrl,
  };
};
